import { type GetShiftDto } from "@clipboard-health/contract-backend-main";
import { z } from "zod";

/**
 * Shift status filter values for the guided support flow.
 * Used to filter shifts by their current status.
 */
export const ShiftStatusFilter = {
  UPCOMING: "upcoming",
  PAST: "past",
} as const;

export type ShiftStatusFilterType = (typeof ShiftStatusFilter)[keyof typeof ShiftStatusFilter];

/**
 * Shift completion status for display in the support flow.
 * Time-based only: past or upcoming (future).
 * Cancellation is an orthogonal concern signaled by deleted === true.
 */
export const ShiftCompletionStatus = {
  PAST: "past",
  UPCOMING: "upcoming",
} as const;

export type ShiftCompletionStatusType =
  (typeof ShiftCompletionStatus)[keyof typeof ShiftCompletionStatus];

const shiftCompletionStatusValues = [
  ShiftCompletionStatus.PAST,
  ShiftCompletionStatus.UPCOMING,
] as const;

/**
 * Payment status for past shifts.
 */
export const PaymentStatus = {
  PAID: "paid",
  PENDING: "pending",
  PAYMENT_ISSUE: "payment_issue",
} as const;

export type PaymentStatusType = (typeof PaymentStatus)[keyof typeof PaymentStatus];

/**
 * Cancellation details as returned by the V3 shifts API.
 * Derived from the contract package to ensure type alignment with the backend.
 */
export type ApiCancellationDetails = NonNullable<
  NonNullable<GetShiftDto["attributes"]>["cancellationDetails"]
>;

/**
 * Shared Zod schema for cancellation details, matching the V3 API shape.
 * Used by both supportShiftSchema and ShiftAttributesSchema to avoid duplication.
 */
export const cancellationDetailsSchema = z.object({
  cancelledAt: z.string(),
  reason: z
    .object({
      type: z.string(),
      description: z.string(),
    })
    .optional(),
  isWorkerPaidForCancellation: z.boolean().optional(),
  isSentHome: z.boolean().optional(),
}) satisfies z.ZodType<ApiCancellationDetails>;

/**
 * Schema for a shift displayed in the interactive support shift selection list.
 * Includes active shifts and facility-cancelled shifts.
 *
 * Money values are stored in cents to avoid floating-point precision issues.
 * Time values are stored in hours as decimals for calculations.
 * Formatting to display strings happens in the UI layer only.
 */
export const supportShiftSchema = z.object({
  id: z.string(),
  workplaceName: z.string(),
  startAt: z.string(),
  endAt: z.string(),
  timezone: z.string(),
  status: z.enum(shiftCompletionStatusValues),
  /** Whether the shift was cancelled (deleted) by the workplace. */
  deleted: z.boolean(),
  /** Hours worked as a decimal number (e.g., 8.5 for 8h 30m). Formatting happens in UI. */
  hoursWorkedInHours: z.number().optional(),
  /** Payment amount in cents to avoid float math issues. Formatting happens in UI. */
  paymentAmountInCents: z.number().optional(),
  paymentStatus: z
    .enum([PaymentStatus.PAID, PaymentStatus.PENDING, PaymentStatus.PAYMENT_ISSUE])
    .optional(),
  /** Cancellation metadata. Only present for cancelled shifts. Matches the V3 API shape. */
  cancellationDetails: cancellationDetailsSchema.optional(),
});

export type SupportShift = z.infer<typeof supportShiftSchema>;

/**
 * Details specific to a cancelled shift, used by the cancelled shift details screen.
 * Extends the base cancellation fields from the API contract with display context.
 */
export interface CancelledShiftDetails {
  shiftId: string;
  workplaceName: string;
  /** ISO datetime of the shift start */
  startAt: string;
  /** ISO datetime of the shift end */
  endAt: string;
  timezone: string;
  /** Cancellation metadata grouped to match the backend API shape. */
  cancellationDetails: ApiCancellationDetails;
}

/**
 * Props for the shift card component in the support flow.
 */
export interface SupportShiftCardProps {
  shift: SupportShift;
  onSelect: (shiftId: string) => void;
}

/**
 * Schema for the guided support page route params.
 */
export const guidedSupportRouteParamsSchema = z.object({});

export type GuidedSupportRouteParams = z.infer<typeof guidedSupportRouteParamsSchema>;

/**
 * Schema for the shift details page route params.
 */
export const shiftDetailsRouteParamsSchema = z.object({
  shiftId: z.string(),
});

export type ShiftDetailsRouteParams = z.infer<typeof shiftDetailsRouteParamsSchema>;
